import decode from 'heic-decode';

/**
 * Returns the effective MIME type for a file.
 * Browsers on Windows often report an empty type for HEIC files
 * because the HEIC MIME type isn't registered in the OS.
 * This helper inspects the file extension as a fallback.
 */
export const getEffectiveFileType = (file: File): string => {
  if (file.type) return file.type;

  const ext = file.name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'heic': return 'image/heic';
    case 'heif': return 'image/heif';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png': return 'image/png';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    default: return 'application/octet-stream';
  }
};

/**
 * Returns a File with the correct MIME type.
 * If the browser-reported type is already correct, returns the original file.
 * Otherwise creates a new File with the effective type from the extension.
 */
export const normalizeFileForUpload = (file: File): File => {
  const effectiveType = getEffectiveFileType(file);
  if (file.type === effectiveType) return file;
  return new File([file], file.name, { type: effectiveType });
};

/**
 * Checks whether a File is a HEIC/HEIF image by MIME type or extension.
 */
export const isHeicFile = (file: File): boolean => {
  const type = file.type.toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
};

/**
 * Converts a HEIC/HEIF blob to a JPEG blob using heic-decode (libheif WASM) + canvas.
 * heic-decode decodes to raw RGBA pixel data, which we draw on an off-screen canvas
 * and export as high-quality JPEG.
 *
 * This is the single shared implementation used by both:
 * - imageLoaderService (full-res photo display)
 * - preview generation (album creation thumbnails)
 */
export const convertHeicBlobToJpeg = async (heicBlob: Blob, quality: number = 0.92): Promise<Blob> => {
  const arrayBuffer = await heicBlob.arrayBuffer();
  const buffer = new Uint8Array(arrayBuffer);

  const { width, height, data } = await decode({ buffer });

  if (!data || !width || !height) {
    throw new Error('heic-decode returned empty image data');
  }

  const imageData = new ImageData(new Uint8ClampedArray(data), width, height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to create canvas 2D context');
  }

  ctx.putImageData(imageData, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('canvas.toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
};

/**
 * Creates a preview blob URL for a file.
 * For HEIC files on browsers without native HEIC support, this converts
 * the file to JPEG first so the `<img>` tag can render it.
 * For other files, returns a direct object URL.
 *
 * @param file - The image file
 * @param heicSupported - Whether the browser supports HEIC natively
 * @returns A blob URL suitable for use in <img src>
 */
export const createPreviewUrl = async (file: File, heicSupported: boolean): Promise<string> => {
  if (isHeicFile(file) && !heicSupported) {
    try {
      const jpegBlob = await convertHeicBlobToJpeg(file, 0.8);
      return URL.createObjectURL(jpegBlob);
    } catch (err) {
      console.warn(`Failed to convert HEIC preview for ${file.name}:`, err);
      // Fall through to direct object URL — will show as broken on unsupported browsers
    }
  }
  return URL.createObjectURL(file);
};

/**
 * Converts a File to a base64 string (without the data URL prefix).
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result && typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = error => reject(error);
  });
};

